import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, BarChart2, Users, Award,
  XCircle, Clock, ArrowUpRight, ArrowDownRight,
  Calendar, Layers, ChevronRight, Activity
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import { APP, STATUS_COLORS, IconPill } from './DashbordApplicationTab';

/* ─── Helpers ────────────────────────────────────────────────────────────── */
const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

const tooltipStyle = {
  borderRadius: 10,
  border: 'none',
  boxShadow: '0 4px 20px rgba(0,0,0,0.10)',
  fontSize: 12,
  backgroundColor: '#fff',
  color: APP.textMd,
};

/* ─── Time filter toggle ─────────────────────────────────────────────────── */
const TimeToggle = ({ value, onChange }) => (
  <div style={{ border: `1px solid ${APP.border}` }} className="flex rounded-xl overflow-hidden self-start">
    {['7d', '14d'].map((t) => (
      <button
        key={t}
        onClick={() => onChange(t)}
        style={
          value === t
            ? { backgroundColor: APP.primary, color: '#fff' }
            : { backgroundColor: APP.card, color: APP.textMd }
        }
        className="px-3 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
      >
        {t.toUpperCase()}
      </button>
    ))}
  </div>
);

/* ─── Stat change chip ───────────────────────────────────────────────────── */
const ChangePill = ({ value, suffix = '%' }) => {
  const up = value >= 0;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{
        backgroundColor: up ? APP.greenBg : APP.redBg,
        color: up ? APP.green : APP.red,
      }}
    >
      {up ? <ArrowUpRight size={10} /> : <ArrowDownRight size={10} />}
      {Math.abs(value)}{suffix}
    </span>
  );
};

/* ─── KPI summary cards ──────────────────────────────────────────────────── */
const TrendKPICards = ({ dashboardData }) => {
  const totalApps  = dashboardData?.totalApplications  ?? 0;
  const totalJobs  = dashboardData?.totalJobs          ?? 0;
  const accepted   = dashboardData?.jobsByStatus?.find(s => s.status === 'accepted')?.count ?? 0;
  const rejected   = dashboardData?.jobsByStatus?.find(s => s.status === 'rejected')?.count ?? 0;
  const pending    = dashboardData?.jobsByStatus?.find(s => s.status === 'pending')?.count  ?? 0;
  const acceptRate = totalApps ? +((accepted / totalApps) * 100).toFixed(1) : 0;
  const rejRate    = totalApps ? +((rejected / totalApps) * 100).toFixed(1) : 0;
  const appPerJob  = totalJobs ? +(totalApps / totalJobs).toFixed(1) : 0;

  const CARDS = [
    {
      label: 'Total applications',
      value: totalApps,
      sub: `${appPerJob} per job`,
      icon: Users,
      bg: APP.primaryBg,
      color: APP.primary,
      change: null,
    },
    {
      label: 'Acceptance rate',
      value: `${acceptRate}%`,
      sub: `${accepted} offers extended`,
      icon: Award,
      bg: APP.greenBg,
      color: APP.green,
      change: acceptRate,
    },
    {
      label: 'Rejection rate',
      value: `${rejRate}%`,
      sub: `${rejected} not selected`,
      icon: XCircle,
      bg: APP.redBg,
      color: APP.red,
      change: -rejRate,
    },
    {
      label: 'Pending decisions',
      value: pending,
      sub: 'Awaiting review',
      icon: Clock,
      bg: APP.amberBg,
      color: APP.amber,
      change: null,
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      {CARDS.map(({ label, value, sub, icon, bg, color, change }) => (
        <div
          key={label}
          style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
          className="rounded-2xl p-4 flex flex-col gap-3 hover:shadow-sm transition-shadow"
        >
          <IconPill icon={icon} bg={bg} color={color} />
          <div>
            <p style={{ color: APP.text }} className="text-2xl font-bold leading-none">{value}</p>
            <p style={{ color: APP.textSm }} className="text-xs mt-1">{label}</p>
          </div>
          {change !== null
            ? <ChangePill value={change} />
            : <p style={{ color: APP.textXs }} className="text-xs">{sub}</p>
          }
        </div>
      ))}
    </div>
  );
};

/* ─── Application trend chart (area) ────────────────────────────────────── */
const AppTrendChart = ({ data, timeFilter, onTimeChange }) => {
  const sliced = timeFilter === '7d' ? data.slice(-7) : data.slice(-14);

  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-1">
        <div>
          <h3 style={{ color: APP.text }} className="text-sm font-semibold">Application volume</h3>
          <p style={{ color: APP.textXs }} className="text-xs">
            Daily submissions — last {timeFilter === '7d' ? '7' : '14'} days
          </p>
        </div>
        <TimeToggle value={timeFilter} onChange={onTimeChange} />
      </div>
      <div className="h-52 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={sliced} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <defs>
              <linearGradient id="areaGradTrend" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={APP.primary} stopOpacity={0.15} />
                <stop offset="95%" stopColor={APP.primary} stopOpacity={0}    />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={APP.borderLight} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDay}
              stroke={APP.border}
              tick={{ fontSize: 11, fill: APP.textXs }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              stroke={APP.border}
              tick={{ fontSize: 11, fill: APP.textXs }}
              axisLine={false} tickLine={false}
            />
            <Tooltip
              formatter={(v) => [`${v} applications`, 'Applications']}
              labelFormatter={(l) => fmtDay(l)}
              contentStyle={tooltipStyle}
            />
            <Area
              type="monotone" dataKey="count"
              stroke={APP.primary} strokeWidth={2.5}
              fill="url(#areaGradTrend)"
              dot={false} activeDot={{ r: 5, fill: APP.primary }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ─── Status breakdown donut ─────────────────────────────────────────────── */
const StatusDonut = ({ jobsByStatus }) => (
  <div
    style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
    className="rounded-2xl p-5"
  >
    <h3 style={{ color: APP.text }} className="text-sm font-semibold mb-1">Status breakdown</h3>
    <p style={{ color: APP.textXs }} className="text-xs mb-3">All applications by outcome</p>
    <div className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={jobsByStatus}
            cx="50%" cy="50%"
            innerRadius={52} outerRadius={76}
            paddingAngle={3} dataKey="count" strokeWidth={0}
          >
            {jobsByStatus.map((entry, i) => (
              <Cell key={i} fill={STATUS_COLORS[entry.status] || APP.border} />
            ))}
          </Pie>
          <Tooltip
            formatter={(v, _, p) => [`${v}`, p.payload.status]}
            contentStyle={tooltipStyle}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
    <div className="space-y-2 mt-1">
      {jobsByStatus.map((s) => {
        const total = jobsByStatus.reduce((acc, x) => acc + x.count, 0);
        const pct   = total ? ((s.count / total) * 100).toFixed(0) : 0;
        return (
          <div key={s.status} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: STATUS_COLORS[s.status] }}
              />
              <span style={{ color: APP.textMd }} className="text-xs capitalize">{s.status}</span>
            </div>
            <div className="flex items-center gap-2">
              <span style={{ color: APP.textXs }} className="text-[10px]">{pct}%</span>
              <span style={{ color: APP.text }} className="text-xs font-semibold">{s.count}</span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─── Top jobs bar chart ─────────────────────────────────────────────────── */
const TopJobsChart = ({ applicationsByJob }) => {
  const data = (applicationsByJob || []).slice(0, 6);
  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 style={{ color: APP.text }} className="text-sm font-semibold">Top jobs by applications</h3>
          <p style={{ color: APP.textXs }} className="text-xs">Most applied positions</p>
        </div>
      </div>
      {!data.length ? (
        <p style={{ color: APP.textXs }} className="text-sm text-center py-10">No data yet</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 0, right: 16, left: 0, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category" dataKey="jobTitle" width={90}
                tick={{ fontSize: 11, fill: APP.textSm }}
                tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + '…' : v}
                axisLine={false} tickLine={false}
              />
              <Tooltip
                formatter={(v) => [`${v}`, 'Applications']}
                contentStyle={tooltipStyle}
              />
              <Bar dataKey="applicationCount" fill={APP.primary} barSize={10} radius={[0, 4, 4, 0]}>
                {data.map((_, i) => (
                  <Cell
                    key={i}
                    fill={i === 0 ? APP.primary : `${APP.primary}${Math.round(220 - i * 28).toString(16)}`}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

/* ─── Cumulative line chart ──────────────────────────────────────────────── */
const CumulativeChart = ({ data, timeFilter }) => {
  const sliced = (timeFilter === '7d' ? data.slice(-7) : data.slice(-14));

  /* build cumulative count */
  let running = 0;
  const cumulative = sliced.map((d) => {
    running += d.count ?? 0;
    return { ...d, cumulative: running };
  });

  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl p-5"
    >
      <div className="mb-1">
        <h3 style={{ color: APP.text }} className="text-sm font-semibold">Cumulative applications</h3>
        <p style={{ color: APP.textXs }} className="text-xs">Running total over selected period</p>
      </div>
      <div className="h-52 mt-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={cumulative} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={APP.borderLight} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={fmtDay}
              stroke={APP.border}
              tick={{ fontSize: 11, fill: APP.textXs }}
              axisLine={false} tickLine={false}
            />
            <YAxis
              stroke={APP.border}
              tick={{ fontSize: 11, fill: APP.textXs }}
              axisLine={false} tickLine={false}
            />
            <Tooltip
              formatter={(v) => [`${v}`, 'Total applications']}
              labelFormatter={(l) => fmtDay(l)}
              contentStyle={tooltipStyle}
            />
            <Line
              type="monotone" dataKey="cumulative"
              stroke={APP.purple ?? '#7c3aed'} strokeWidth={2.5}
              dot={false} activeDot={{ r: 5, fill: APP.purple ?? '#7c3aed' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/* ─── Quick insights panel ───────────────────────────────────────────────── */
const InsightsPanel = ({ dashboardData }) => {
  const totalApps = dashboardData?.totalApplications ?? 0;
  const totalJobs = dashboardData?.totalJobs         ?? 0;
  const accepted  = dashboardData?.jobsByStatus?.find(s => s.status === 'accepted')?.count ?? 0;
  const rejected  = dashboardData?.jobsByStatus?.find(s => s.status === 'rejected')?.count ?? 0;
  const pending   = dashboardData?.jobsByStatus?.find(s => s.status === 'pending')?.count  ?? 0;
  const topJob    = [...(dashboardData?.applicationsByJob ?? [])].sort((a, b) => b.applicationCount - a.applicationCount)[0];

  const rows = [
    { label: 'Total applications',  value: totalApps },
    { label: 'Total jobs posted',   value: totalJobs  },
    { label: 'Offers accepted',     value: accepted   },
    { label: 'Candidates rejected', value: rejected   },
    { label: 'Pending review',      value: pending    },
    { label: 'Acceptance rate',     value: totalApps ? `${((accepted / totalApps) * 100).toFixed(1)}%` : '—' },
    { label: 'Rejection rate',      value: totalApps ? `${((rejected / totalApps) * 100).toFixed(1)}%` : '—' },
    { label: 'Apps per job',        value: totalJobs  ? (totalApps / totalJobs).toFixed(1) : '—' },
  ];

  return (
    <div
      style={{ backgroundColor: APP.card, border: `1px solid ${APP.border}` }}
      className="rounded-2xl p-5"
    >
      <h3 style={{ color: APP.text }} className="text-sm font-semibold mb-4">Quick insights</h3>

      <div className="space-y-0 divide-y" style={{ borderColor: APP.borderLight }}>
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between py-2.5">
            <span style={{ color: APP.textSm }} className="text-xs">{label}</span>
            <span style={{ color: APP.text }} className="text-sm font-bold">{value}</span>
          </div>
        ))}
      </div>

      {topJob && (
        <div
          style={{ backgroundColor: APP.primaryBg, border: `1px solid ${APP.primary}22` }}
          className="rounded-xl px-3 py-2.5 mt-4 flex items-center gap-2"
        >
          <TrendingUp size={13} style={{ color: APP.primary }} />
          <div className="min-w-0">
            <p style={{ color: APP.primary }} className="text-[10px] font-semibold uppercase tracking-wide">
              Top performing job
            </p>
            <p style={{ color: APP.text }} className="text-xs font-bold truncate mt-0.5">
              {topJob.jobTitle}
            </p>
            <p style={{ color: APP.textXs }} className="text-[10px]">
              {topJob.applicationCount} application{topJob.applicationCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main TrendsTab ─────────────────────────────────────────────────────── */
const TrendsTab = ({ dashboardData }) => {
  const [timeFilter, setTimeFilter] = useState('14d');

  const trend      = dashboardData?.applicationTrend    ?? [];
  const byStatus   = dashboardData?.jobsByStatus        ?? [];
  const byJob      = dashboardData?.applicationsByJob   ?? [];

  return (
    <div>
      {/* Section header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div>
          <h2 style={{ color: APP.text }} className="text-base font-semibold">
            Analytics & Trends
          </h2>
          <p style={{ color: APP.textXs }} className="text-xs mt-0.5">
            Performance overview across all your job postings
          </p>
        </div>
        <TimeToggle value={timeFilter} onChange={setTimeFilter} />
      </div>

      {/* KPI row */}
      <TrendKPICards dashboardData={dashboardData} />

      {/* Row 1 — area chart + donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2">
          <AppTrendChart
            data={trend}
            timeFilter={timeFilter}
            onTimeChange={setTimeFilter}
          />
        </div>
        <StatusDonut jobsByStatus={byStatus} />
      </div>

      {/* Row 2 — top jobs bar + cumulative line + insights */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TopJobsChart applicationsByJob={byJob} />
        <CumulativeChart data={trend} timeFilter={timeFilter} />
        <InsightsPanel dashboardData={dashboardData} />
      </div>
    </div>
  );
};

export default TrendsTab;